import { useState } from "react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { CalendarIcon, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Reveal } from "@/components/Reveal";
import { IMAGES, submitBooking } from "@/lib/site";

const TIME_SLOTS = ["Ochtend (09.00–12.00)", "Middag (12.00–17.00)", "Avond (17.00–20.00)"];
const SUPPORT_TYPES = [
    "Crisisbegeleiding jeugd",
    "Crisisbegeleiding LVB",
    "Nazorg & stabilisatie",
    "Overig / weet ik nog niet",
];

export default function Booking() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
    const [date, setDate] = useState(null);
    const [timeSlot, setTimeSlot] = useState("");
    const [supportType, setSupportType] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!date) {
            toast.error("Kies een voorkeursdatum voor het gesprek.");
            return;
        }
        if (!timeSlot || !supportType) {
            toast.error("Kies een voorkeurstijd en type ondersteuning.");
            return;
        }
        setLoading(true);
        try {
            await submitBooking({
                ...form,
                preferred_date: format(date, "yyyy-MM-dd"),
                time_slot: timeSlot,
                support_type: supportType,
            });
            setSuccess(true);
            toast.success("Uw aanvraag is verzonden. Ik neem binnen 24 uur contact op.");
        } catch {
            toast.error("Verzenden is niet gelukt. Probeer het opnieuw of bel direct.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="intake" className="bg-moss-900 py-24 text-sand-50 lg:py-32">
            <div className="mx-auto grid max-w-7xl items-start gap-14 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
                <div className="lg:sticky lg:top-28">
                    <Reveal>
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-terracotta">
                            Acute situatie? Bel direct
                        </p>
                        <h2 className="mt-4 font-serif text-3xl font-semibold leading-snug tracking-tight sm:text-4xl">
                            Plan een gratis intakegesprek
                        </h2>
                        <p className="mt-6 max-w-lg text-base leading-relaxed text-sand-50/75">
                            Ook in een dreigende of acute crisis kunt u contact opnemen. Vul
                            het formulier in en ik reageer zo snel mogelijk — bij spoed heeft
                            bellen of appen de voorkeur.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="mt-10 overflow-hidden rounded-t-[8rem] rounded-b-3xl border border-sand-50/15">
                            <img
                                src={IMAGES.hands}
                                alt="Ondersteunende handen als symbool van zorg"
                                className="h-72 w-full object-cover"
                            />
                        </div>
                        <ul className="mt-8 space-y-3 text-sm text-sand-50/80">
                            <li className="flex items-center gap-3">
                                <CheckCircle2 size={16} className="text-terracotta" />
                                100% gratis en vrijblijvend
                            </li>
                            <li className="flex items-center gap-3">
                                <CheckCircle2 size={16} className="text-terracotta" />
                                Bij u thuis, op locatie of telefonisch
                            </li>
                            <li className="flex items-center gap-3">
                                <CheckCircle2 size={16} className="text-terracotta" />
                                Advies over Jeugdwet en Wmo
                            </li>
                            <li className="flex items-center gap-3">
                                <CheckCircle2 size={16} className="text-terracotta" />
                                Ook in opdracht van gemeenten, wijkteams en instellingen
                            </li>
                        </ul>
                    </Reveal>
                </div>

                <Reveal delay={0.1}>
                    <div className="rounded-3xl bg-white p-7 text-moss-900 shadow-2xl sm:p-10">
                        {success ? (
                            <div data-testid="intake-success-message" className="flex flex-col items-center py-16 text-center">
                                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-moss-100 text-moss-800">
                                    <CheckCircle2 size={32} />
                                </span>
                                <h3 className="mt-6 font-serif text-3xl font-semibold">Aanvraag ontvangen!</h3>
                                <p className="mt-3 max-w-sm text-sm leading-relaxed text-moss-900/70">
                                    Bedankt voor uw aanvraag. Ik neem binnen 24 uur contact met
                                    u op om het intakegesprek definitief in te plannen.
                                </p>
                                <button
                                    onClick={() => {
                                        setSuccess(false);
                                        setForm({ name: "", email: "", phone: "", notes: "" });
                                        setDate(null);
                                        setTimeSlot("");
                                        setSupportType("");
                                    }}
                                    data-testid="intake-new-request-button"
                                    className="mt-8 rounded-full border border-line px-6 py-3 text-sm font-semibold transition-colors hover:border-terracotta hover:text-terracotta"
                                >
                                    Nieuwe aanvraag doen
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={onSubmit} data-testid="booking-intake-form" className="space-y-5">
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="intake-name">Volledige naam</Label>
                                        <Input
                                            id="intake-name"
                                            data-testid="intake-input-name"
                                            required
                                            value={form.name}
                                            onChange={set("name")}
                                            placeholder="Uw naam"
                                            className="h-12"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="intake-phone">Telefoonnummer</Label>
                                        <Input
                                            id="intake-phone"
                                            data-testid="intake-input-phone"
                                            required
                                            type="tel"
                                            value={form.phone}
                                            onChange={set("phone")}
                                            placeholder="06 12345678"
                                            className="h-12"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="intake-email">E-mailadres</Label>
                                    <Input
                                        id="intake-email"
                                        data-testid="intake-input-email"
                                        required
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="u@voorbeeld.nl"
                                        className="h-12"
                                    />
                                </div>
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label>Voorkeursdatum</Label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    data-testid="intake-input-date"
                                                    className="h-12 w-full justify-start text-left font-normal"
                                                >
                                                    <CalendarIcon className="mr-2 h-4 w-4 text-terracotta" />
                                                    {date ? format(date, "d MMMM yyyy", { locale: nl }) : "Kies een datum"}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={date}
                                                    onSelect={setDate}
                                                    disabled={{ before: new Date() }}
                                                    locale={nl}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Voorkeurstijd</Label>
                                        <Select value={timeSlot} onValueChange={setTimeSlot}>
                                            <SelectTrigger data-testid="intake-select-time" className="h-12">
                                                <SelectValue placeholder="Kies een dagdeel" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {TIME_SLOTS.map((slot) => (
                                                    <SelectItem key={slot} value={slot} data-testid={`intake-time-${slot.slice(0, 7).toLowerCase()}`}>
                                                        {slot}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Type ondersteuning</Label>
                                    <Select value={supportType} onValueChange={setSupportType}>
                                        <SelectTrigger data-testid="intake-select-type" className="h-12">
                                            <SelectValue placeholder="Waar kan ik u mee helpen?" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SUPPORT_TYPES.map((type) => (
                                                <SelectItem key={type} value={type}>
                                                    {type}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="intake-notes">Opmerkingen of vragen</Label>
                                    <Textarea
                                        id="intake-notes"
                                        data-testid="intake-input-notes"
                                        value={form.notes}
                                        onChange={set("notes")}
                                        placeholder="Vertel kort over uw situatie of vraag (optioneel)"
                                        rows={4}
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="intake-submit-button"
                                    className="h-13 w-full rounded-full bg-terracotta py-6 text-sm font-semibold text-sand-50 transition-all duration-300 hover:bg-terracotta-dark"
                                >
                                    {loading ? (
                                        <span className="flex items-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Verzenden...
                                        </span>
                                    ) : (
                                        "Intakegesprek aanvragen"
                                    )}
                                </Button>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
